import {
  Alert,
  Button,
  Drawer,
  HStack,
  Heading,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCallback } from 'react';
import { HiX } from 'react-icons/hi';

import { DocumentThreadPicker } from './document-thread-picker';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import { MessageList } from '#src/components/message-list';
import { useDocumentScopedMessages } from '#src/hooks/use-document-scoped-messages';
import { useCurrentUser } from '#src/providers/user-provider';

interface DocumentMessagesDrawerProps {
  documentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DocumentMessagesDrawer({
  documentId,
  open,
  onOpenChange,
}: DocumentMessagesDrawerProps) {
  const user = useCurrentUser();
  const {
    messageText,
    setMessageText,
    clearMessageDraft,
    sortedMessages,
    displayMessages,
    focusedRootId,
    isMessagesInitialLoad,
    messagesError,
    refetchMessages,
    handleRunChatMessageQuery,
    chatMessageQueryLoadingId,
    handleSendMessage,
    isSendingMessage,
    expandThread,
    collapseThread,
    replaceStripFocusParam,
    messagesFeatureActive,
  } = useDocumentScopedMessages(documentId, { enabled: open });

  const handleClose = useCallback(() => {
    clearMessageDraft();
    onOpenChange(false);
  }, [clearMessageDraft, onOpenChange]);

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      placement="end"
      size="md"
    >
      <Drawer.Backdrop />
      <Drawer.Positioner>
        <Drawer.Content display="flex" flexDirection="column">
          <Drawer.Header
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Heading size="md" color="blue.600">
              Document messages
            </Heading>
            <IconButton
              aria-label="Close document messages"
              variant="ghost"
              size="sm"
              onClick={handleClose}
            >
              <HiX />
            </IconButton>
          </Drawer.Header>
          <Drawer.Body
            display="flex"
            flexDirection="column"
            gap={4}
            overflow="hidden"
            flex={1}
            minHeight={0}
          >
            {!user.email ? (
              <Alert.Root status="info">
                <Alert.Indicator />
                <Alert.Title>
                  Please set your user email in Settings to view messages.
                </Alert.Title>
              </Alert.Root>
            ) : (
              <MessageList
                messages={displayMessages}
                loading={isMessagesInitialLoad}
                error={messagesError ?? undefined}
                emptyMessage="No messages for this document yet."
                focusedRootId={focusedRootId}
                onExpandThread={expandThread}
                onRunChatMessageQuery={handleRunChatMessageQuery}
                chatMessageQueryLoadingId={chatMessageQueryLoadingId}
                onMessageDeleted={() => void refetchMessages()}
              />
            )}

            {user.email && messagesFeatureActive && (
              <VStack gap={2} align="stretch" flexShrink={0}>
                {focusedRootId && (
                  <HStack gap={2} align="center" fontSize="xs" color="fg.muted">
                    <Text>Replying in thread</Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => collapseThread()}
                    >
                      Cancel
                    </Button>
                  </HStack>
                )}
                <ChatComposerRow
                  value={messageText}
                  onChange={setMessageText}
                  onSubmit={() => void handleSendMessage()}
                  placeholder={
                    focusedRootId ? 'Type a reply…' : 'Type a message…'
                  }
                  loading={isSendingMessage}
                  trailingActions={
                    <DocumentThreadPicker
                      roots={sortedMessages}
                      focusedRootId={focusedRootId}
                      onSelectRoot={expandThread}
                      onClearThreadFocus={replaceStripFocusParam}
                      disabled={isSendingMessage}
                    />
                  }
                />
              </VStack>
            )}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}
